import { FC, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { Helmet } from 'react-helmet-async'
import PageTitleWrapper from '../../../components/PageTitleWrapper'
import RecentBillsHeader from './PageHeader'
import PageContainerWrapper from '../../../components/PageContainerWrapper'
import { Box, Step, StepButton, StepLabel, Stepper } from '@mui/material'
import { showAlertAction } from '../../../store/alerts'
import { useNavigate } from 'react-router-dom'
import { getRecentBillsDataAction, getRecentBillsDataResetAction } from './actions'
import { DEFAULT_RECENT_BILLS_SORT } from './SortBy'
import { BulkInvoicePreviewItem } from './types'
import SelectSitesStep from './SelectSitesStep'
import InvoicesDatesStep, { InvoicesDatesStepHandle } from './InvoicesDatesStep'
import CreateInvoicesStep, { CreateInvoicesStepHandle } from './CreateInvoicesStep'
import BulkInvoicesStepperFooter from './BulkInvoicesStepperFooter'
import ApplicationLinks from '../../../ApplicationLinks'

export interface RecentBillsProps {}

const STEPS = ['Select Sites', 'Invoices Date', 'Create Invoices'] as const

export const ALL_BILLS_VIEW_QUERY = 'gst_type=ALL&sort_by=CREATE_DATE_DESC'

export const RecentBills: FC<RecentBillsProps> = () => {
  const navigate = useNavigate()
  const dispatchAction = useAppDispatch()
  const { data: sites } = useAppSelector((state) => state.recentBillsState)

  const [activeStep, setActiveStep] = useState(0)
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>([])
  const [sortType, setSortType] = useState(DEFAULT_RECENT_BILLS_SORT)
  const [previewItems, setPreviewItems] = useState<BulkInvoicePreviewItem[] | null>(
    null
  )
  const [billFormat, setBillFormat] = useState<string | null>(null)
  const [creationStarted, setCreationStarted] = useState(false)
  const [isCreationComplete, setIsCreationComplete] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [invoiceCount, setInvoiceCount] = useState(0)
  const [isCreating, setIsCreating] = useState(false)

  const datesStepRef = useRef<InvoicesDatesStepHandle>(null)
  const createStepRef = useRef<CreateInvoicesStepHandle>(null)

  useEffect(() => {
    dispatchAction(getRecentBillsDataResetAction())
    dispatchAction(getRecentBillsDataAction(sortType))
  }, [])

  useEffect(() => {
    setSelectedSiteIds([])
    setPreviewItems(null)
    setBillFormat(null)
    setCreationStarted(false)
    setIsCreationComplete(false)
    setPreviewError(null)
    if (activeStep > 0) {
      setActiveStep(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites])

  const refreshList = () => {
    dispatchAction(getRecentBillsDataAction(sortType))
  }

  const handleSortChange = (type: string) => {
    setSortType(type)
    dispatchAction(getRecentBillsDataAction(type))
  }

  const canGoToStep = (step: number): boolean => {
    if (creationStarted && step < 2) {
      return false
    }
    if (step === 0) return true
    if (step === 1) {
      return selectedSiteIds.length >= 1
    }
    if (step === 2) {
      return (
        selectedSiteIds.length >= 1 &&
        previewItems != null &&
        previewItems.length > 0 &&
        billFormat != null
      )
    }
    return false
  }

  const clearPreviewState = () => {
    setPreviewItems(null)
    setBillFormat(null)
    setCreationStarted(false)
    setIsCreationComplete(false)
  }

  const goToStep = (step: number) => {
    if (!canGoToStep(step)) return
    if (activeStep === 2 && step < 2 && !creationStarted) {
      clearPreviewState()
    }
    setActiveStep(step)
    setPreviewError(null)
  }

  const handlePrev = () => {
    if (activeStep === 0) return
    if (activeStep === 2 && creationStarted) return
    if (activeStep === 2) {
      clearPreviewState()
    }
    setActiveStep((s) => s - 1)
    setPreviewError(null)
  }

  const handleNext = async () => {
    if (activeStep === 0) {
      if (selectedSiteIds.length < 1) {
        dispatchAction(
          showAlertAction({
            message: 'Please select at least one site to continue.',
            severity: 'error',
          })
        )
        return
      }
      setActiveStep(1)
      setPreviewError(null)
      return
    }

    if (activeStep === 1) {
      setPreviewLoading(true)
      setPreviewError(null)
      const result = await datesStepRef.current?.runPreview()
      setPreviewLoading(false)
      if (!result || !result.ok) {
        setPreviewError(result && !result.ok ? result.error : 'Preview failed.')
        return
      }
      setPreviewItems(result.items)
      setBillFormat(result.billFormat)
      setCreationStarted(false)
      setIsCreationComplete(false)
      setActiveStep(2)
      return
    }

    if (activeStep === 2) {
      if (isCreationComplete) {
        navigate(`${ApplicationLinks.allBillsLink()}?${ALL_BILLS_VIEW_QUERY}`)
        refreshList()
        return
      }
      if (creationStarted || isCreating) return
      setCreationStarted(true)
      await createStepRef.current?.startCreate()
    }
  }

  const getNextLabel = (): string => {
    if (activeStep === 2) {
      if (isCreationComplete) {
        return 'View Invoices'
      }
      const count = createStepRef.current?.getInvoiceCount() ?? invoiceCount
      return `Create ${count} Invoice${count === 1 ? '' : 's'}`
    }
    return 'Next'
  }

  const isNextDisabled = (): boolean => {
    if (activeStep === 1) {
      return previewLoading
    }
    if (activeStep === 2) {
      if (isCreationComplete) {
        return false
      }
      if (isCreating || creationStarted) {
        return isCreating
      }
      const count = createStepRef.current?.getInvoiceCount() ?? invoiceCount
      return count === 0
    }
    return false
  }

  const showPrev = activeStep > 0
  const prevDisabled = activeStep === 2 && creationStarted

  return (
    <>
      <Helmet>
        <title>Create Bulk Invoices</title>
      </Helmet>
        <PageTitleWrapper>
        <RecentBillsHeader />
      </PageTitleWrapper>
      
        <Box
          sx={{
            ml:3,
            mr:3,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 'calc(vh200 - 50px)',
          }}
        >
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              px: 2,
              py: 2,
              mt: '-10px',
              backgroundColor: 'transparent',
              '& .MuiStepLabel-root .Mui-completed': {
                color: '#2e7d32',
              },
              '& .MuiStepLabel-label.Mui-completed': {
                color: '#2e7d32',
                fontWeight: 600,
              },
              '& .MuiStepIcon-root.Mui-completed': {
                color: '#2e7d32',
              },
            }}
          >
            {STEPS.map((label, index) => (
              <Step key={label} completed={index < activeStep}>
                <StepButton
                  onClick={() => goToStep(index)}
                  disabled={!canGoToStep(index)}
                >
                  <StepLabel>{label}</StepLabel>
                </StepButton>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pb: 1 }}>
            <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
              <SelectSitesStep
                sites={sites}
                selectedSiteIds={selectedSiteIds}
                onSelectedSiteIdsChange={setSelectedSiteIds}
              />
            </Box>
            <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
              <InvoicesDatesStep
                ref={datesStepRef}
                selectedSiteIds={selectedSiteIds}
                externalError={previewError}
                loading={previewLoading}
              />
            </Box>
            {activeStep === 2 && previewItems && billFormat && (
              <CreateInvoicesStep
                ref={createStepRef}
                items={previewItems}
                billFormat={billFormat}
                onInvoiceCountChange={setInvoiceCount}
                onCreatingChange={setIsCreating}
                onCompleteChange={setIsCreationComplete}
              />
            )}
          </Box>

          <BulkInvoicesStepperFooter
            showPrev={showPrev}
            prevDisabled={prevDisabled}
            nextLabel={getNextLabel()}
            nextDisabled={isNextDisabled()}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        </Box>
      
    </>
  )
}

export default RecentBills
